import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, type PortalDepartment, type ProjectRef, type TicketDetail } from '../lib/api';
import { priorityLabel, useI18n } from '../lib/i18n';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Select, Textarea, useToast } from '../components/ui';
import { AttachmentsField, useUploads } from '../components/Attachments';
import { NameField, hasDescription, nameFits } from '../components/NameField';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/**
 * Route: `/tickets/new` — the client raises a ticket, to a department of its
 * choice among those it may send to (2026-10-11); it reaches that team at once.
 */
export function TicketFormPage() {
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();

  const [form, setForm] = useState({ subject: '', description: '', projectId: '', priority: 'MEDIUM' });

  const { data: allProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<ProjectRef[]>('/projects'),
  });
  // A ticket goes on a project under way or in its support period, not on one whose support has ended.
  const projects = (allProjects ?? []).filter((p) => p.takesTickets !== false);

  // The departments chosen for the client in the admin. The form starts on the
  // default one, or on the only one; with several, the client picks.
  const departments = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<PortalDepartment[]>('/departments'),
  });
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  if (departmentId === null && departments.data) {
    const start = departments.data.find((d) => d.isDefault) ?? (departments.data.length === 1 ? departments.data[0] : null);
    setDepartmentId(start?.id ?? '');
  }
  // An API from before the choice has no such list: the ticket goes where it always did.
  const choosesDepartment = !departments.isError;

  const create = useMutation({
    mutationFn: () =>
      api.post<TicketDetail>('/tickets', {
        subject: form.subject.trim(),
        description: form.description.trim(),
        projectId: form.projectId || undefined,
        departmentId: choosesDepartment && departmentId ? departmentId : undefined,
        priority: form.priority,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: (ticket) => {
      toast.success(t('tickets.created', { number: ticket.number }));
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      navigate(`/tickets/${ticket.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.code === 'PORTAL_TICKETS_CLOSED') return toast.error(t('dashboard.ticketsShut'));
      if (e instanceof ApiError && (e.details as { field?: string } | undefined)?.field === 'departmentId') {
        // Taken off the client's list, or switched off, while the form was open: offer what is left.
        void queryClient.invalidateQueries({ queryKey: ['departments'] });
        setDepartmentId(null);
        return toast.error(t('tickets.departmentGone'));
      }
      if (e instanceof ApiError && e.code === 'PROJECT_CLOSED') {
        // Its support ended, or it was cancelled, while the form was open: offer what is left.
        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        setForm((f) => ({ ...f, projectId: '' }));
        const cancelled = (e.details as { reason?: string } | undefined)?.reason === 'CANCELLED';
        return toast.error(t(cancelled ? 'tickets.projectCancelled' : 'tickets.projectClosed'));
      }
      toast.error((e as Error).message);
    },
  });

  const needsDepartment = choosesDepartment && (departments.isLoading || !departmentId);

  return (
    <FormPage
      title={t('tickets.new')}
      backTo="/tickets"
      backLabel={t('tickets.title')}
      submitLabel={t('common.send')}
      submitting={create.isPending}
      // Sending while a file is still uploading would leave it behind.
      disabled={!nameFits(form.subject) || !hasDescription(form.description) || uploads.busy || needsDepartment}
      onSubmit={() => create.mutate()}
    >
      <div className="space-y-4">
        <NameField
          label={t('tickets.subject')}
          hint={t('tickets.subjectHint')}
          value={form.subject}
          onChange={(subject) => setForm({ ...form, subject })}
          autoFocus
        />

        <Field label={t('tickets.description')} required hint={t('tickets.descriptionHint')}>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            onPaste={uploads.onPaste}
            className="min-h-[140px]"
          />
        </Field>

        <FormGrid>
          {choosesDepartment && (
            <Field label={t('tickets.department')} required hint={t('tickets.departmentHint')}>
              <Select
                value={departmentId ?? ''}
                onChange={(e) => setDepartmentId(e.target.value)}
                disabled={departments.isLoading}
              >
                {/* Nothing picked for the client when several are open to it and none is the default. */}
                {!departmentId && <option value="">{t('tickets.departmentChoose')}</option>}
                {(departments.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {localized(d)}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field
            label={t('common.project')}
            hint={t(projects.length < (allProjects ?? []).length ? 'tickets.projectHintSupport' : 'tickets.projectHint')}
          >
            <Select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
              <option value="">{t('common.noProject')}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {localized(p)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('common.priority')}>
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityLabel(t, p)}
                </option>
              ))}
            </Select>
          </Field>
        </FormGrid>

        <AttachmentsField uploads={uploads} />
      </div>
    </FormPage>
  );
}
